import { last } from 'lodash';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { IContract, IProject, useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { useMutationDeleteProject } from 'api/mutations/deleteProject.mutate';
import { useQueryGetGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList-2.query';

import { ProjectTreeNode } from 'components/common/TreeProgramFilter/ProjectTree/projectTree.utils';
import { CustomizedConfirmDeleteModal } from 'components/common/index';

import { initialFiltersState } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';

interface Props {
    handleModalClose: () => void;
    isShowModal: boolean;
    callback?: (project: IProject | null, contract: IContract | null) => void;
}

export const ConfirmDeleteProjectModal = ({ handleModalClose, isShowModal, callback }: Props) => {
    const { setVersion } = useVersionStore();

    const { project, update } = useProjectTreeStore();
    const { data: projectHierarchyList } = useQueryGetGeneralHierarchyList();
    const queryClient = useQueryClient();

    const updateReplace = useGlobalFilterDataStore((state) => state.updateReplace);

    const { mutate, isLoading } = useMutationDeleteProject();
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleDeleteProject = () => {
        const projectList = projectHierarchyList?.children;
        if (projectList && projectList.length === 1) {
            handleModalClose();
            handleSnackBar(`Can't delete project. Customer must have at least one project`, 'error');
        } else {
            mutate(
                { id: project?.id },
                {
                    onSuccess: () => {
                        handleSnackBar('Project deleted successfully', 'success');
                        queryClient.invalidateQueries({ queryKey: 'getGeneralHierarchyList' }).then(() => {
                            const newData = queryClient.getQueryData<ProjectTreeNode>('getGeneralHierarchyList');
                            const lastProject: ProjectTreeNode | undefined = last(newData?.children);
                            if (lastProject) {
                                const lastContract: ProjectTreeNode | undefined = last(lastProject?.children);

                                update({
                                    project: {
                                        id: lastProject.id,
                                        title: lastProject.name,
                                        uuid: lastProject.uuid,
                                    },
                                    contract: lastContract
                                        ? {
                                              id: lastContract.id,
                                              title: lastContract.name,
                                              uuid: lastContract.uuid,
                                          }
                                        : null,
                                });

                                if (callback) {
                                    callback(
                                        {
                                            id: lastProject.id,
                                            title: lastProject.name,
                                            uuid: lastProject.uuid,
                                        },
                                        lastContract
                                            ? {
                                                  id: lastContract.id,
                                                  title: lastContract.name,
                                                  uuid: lastContract.uuid,
                                              }
                                            : null,
                                    );
                                }
                            }
                        });

                        setVersion(null);
                        updateReplace(initialFiltersState);

                        sendEventWithDimensions({
                            category: 'Program Library',
                            action: 'Delete Project',
                            label: project?.title,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                    onSettled: () => handleModalClose(),
                },
            );
        }
    };

    return (
        <CustomizedConfirmDeleteModal
            onConfirm={handleDeleteProject}
            hideModal={handleModalClose}
            isShowModal={isShowModal}
            isLoading={isLoading}
            text={`Are you sure you want to delete this project (${project?.title})?`}
        />
    );
};
